import {
  type CellData,
  type Column_Internal,
  type RowData,
  type TableFeature,
  type TableFeatures,
  type TableOptions,
  type Table_Internal,
  type Updater,
  assignPrototypeAPIs,
  assignTableAPIs,
  functionalUpdate,
} from '@tanstack/react-table';

import type {
  ColumnFontStyleFeatureConstructors,
  DataTableColumnFontStyleProps,
  DataTableColumnFontStyleState,
} from './column-font-style-types.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';

/**
 * Configuration hook for the DataTable FontStyle feature.
 * @internal
 */
export function useColumnFontStyle<TData extends DataTableRowData>(
  props: DataTableColumnFontStyleProps,
  options: TableOptions<TDataTableFeatures, Exclude<TData, unknown>>,
): void {
  const configFontStyle = useDataTableConfigElement('fontStyle');

  const [columnFontStyle, onColumnFontStyleChange] = useControllableState({
    value: props.fontStyle ?? configFontStyle,
    defaultValue: props.defaultFontStyle,
    onChange: props.onFontStyleChange,
  });

  // eslint-disable-next-line react-hooks/immutability
  options.state = {
    ...options.state,
    columnFontStyle,
  };
  // eslint-disable-next-line react-hooks/immutability
  options.onColumnFontStyleChange = onColumnFontStyleChange;
}

/**
 * Helper function that will allow access to the columnFontStyle state of an individual column.
 */
function table_setColumnFontStyle<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  updater: Updater<DataTableColumnFontStyleState>,
): void {
  const newValue = functionalUpdate(
    updater,
    table.atoms.columnFontStyle?.get() ?? {},
  );
  table.options.onColumnFontStyleChange?.(newValue);
}

// Add a toggleFontStyle function onto the column so that it can be accessed
// during rendering.
function column_toggleFontStyle<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  column: Column_Internal<TFeatures, TData, TValue>,
  value?: 'code' | 'text',
): void {
  table_setColumnFontStyle(column.table, (old) => {
    let newValue = value;
    // If there is no passed value, apply the toggle logic
    if (newValue === undefined) {
      if (old[column.id] !== undefined) {
        newValue = old[column.id] === 'code' ? 'text' : 'code';
      } else {
        newValue =
          column.table.store.state.visualVariantConfig.fontStyle === 'code'
            ? 'text'
            : 'code';
      }
    }
    return {
      ...old,
      [column.id]: newValue,
    };
  });
}

/**
 * Helper function that will allow access to the columnFontStyle state of an individual column.
 * @returns
 */
function column_getFontStyle<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  column: Column_Internal<TFeatures, TData, TValue>,
): 'code' | 'text' | undefined {
  const fontStyleState = column.table.atoms.columnFontStyle?.get();

  // Check if a font style is defined in the state
  if (fontStyleState?.[column.id]) {
    return fontStyleState?.[column.id];
  }
  // Check if some font style is defined in the columnDef meta based on column types
  return (
    column.columnDef.meta?.fontStyle ??
    column.table.atoms.visualVariantConfig.get().fontStyle
  );
}

export function constructColumnFontStyleFeature(): TableFeature<ColumnFontStyleFeatureConstructors> {
  return {
    getInitialState: (initialState) => {
      return {
        columnFontStyle: structuredClone({}),
        ...initialState,
      };
    },
    assignColumnPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableColumnFontStyleFeature', prototype, table, {
        column_getFontStyle: {
          fn: (column) => column_getFontStyle(column),
        },
        column_toggleFontStyle: {
          fn: (column, value) => column_toggleFontStyle(column, value),
        },
      });
    },
    constructTableAPIs: (table) => {
      assignTableAPIs('DataTableColumnFontStyleFeature', table, {
        table_setColumnFontStyle: {
          fn: (updater) => table_setColumnFontStyle(table, updater),
        },
      });
    },
  };
}

/**
 * Feature implementation for FontStyle. This contains the logic that will be given
 * to the tanstack table. The implementing interface is important as these are
 * the function that will be called by the tanstack table core during render.
 * @internal
 */
export const DataTableColumnFontStyleFeature =
  constructColumnFontStyleFeature();
